import { FC } from "react";

import Stack from "@mui/material/Stack";

import { SHOP_VARIANT_PRESETS, ShopVariantPreset } from "../../helpers/shopVariantPresets";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Cross as Close } from "@/icons/outlined";

interface ShopVariantTypeSelectorProps {
  onSelect: (preset: ShopVariantPreset) => void;
  onCreateFromScratch: () => void;
  onCancel?: () => void;
}

export const ShopVariantTypeSelector: FC<ShopVariantTypeSelectorProps> = ({
  onSelect,
  onCreateFromScratch,
  onCancel,
}) => {
  const { t } = useTranslate();

  return (
    <Stack
      spacing={1}
      sx={(theme) => ({
        backgroundColor: theme.palette.surface.neutral.quiet,
        borderRadius: theme.radius(1),
        padding: 2,
        position: "relative",
      })}
    >
      {onCancel && (
        <DashboardIconButton
          onClick={onCancel}
          size="small"
          vibe="neutral"
          title={t("common", "cancel")}
          sx={(theme) => ({ position: "absolute", top: theme.spacing(1), right: theme.spacing(1) })}
        >
          <Close />
        </DashboardIconButton>
      )}
      <DashboardTypography variant="subtitle2">{t("dashboard", "ticketing.variant.chooseType")}</DashboardTypography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <DashboardTypography variant="body2" vibe="text-neutral-moderate">
          {t("dashboard", "ticketing.variant.suggested")}
        </DashboardTypography>
        {SHOP_VARIANT_PRESETS.map((preset) => (
          <DashboardButton
            key={preset.key}
            variant="outlined"
            size="small"
            vibe="neutral"
            onClick={() => onSelect(preset)}
            sx={(theme) => ({
              borderStyle: "dashed",
              backgroundColor: theme.palette.surface.neutral.white,
            })}
          >
            {t("dashboard", preset.labelKey)}
          </DashboardButton>
        ))}
        <DashboardTypography variant="body2" vibe="text-neutral-moderate">
          {t("dashboard", "ticketing.variant.or")}
        </DashboardTypography>
        <DashboardButton
          variant="outlined"
          size="small"
          vibe="neutral"
          onClick={onCreateFromScratch}
          sx={(theme) => ({
            backgroundColor: theme.palette.surface.neutral.white,
          })}
        >
          {t("dashboard", "ticketing.variant.createFromScratch")}
        </DashboardButton>
      </Stack>
    </Stack>
  );
};
